/**
 * MultiSelectRow.tsx
 *
 * Compact "label left / control right" setting row. Collapsed, it's a single
 * scannable row: the label on the left and a borderless Preview-style pill
 * (gold caret) summarizing the current selection on the right. Clicking the
 * pill opens a floating rounded-row menu ANCHORED WITHIN the row (absolute,
 * not portaled) — a documented exception to the always-portal dropdown rule,
 * because a body-portaled menu inside an already-portaled anchored panel
 * breaks that panel's click-outside dismissal. The host panel must not clip
 * (overflow: visible) and the open row lifts its z-index.
 *
 * Two selection modes: multi (checkboxes, menu stays open) and single
 * (`singleSelect` — gold check, picking closes the menu).
 *
 * See docs/design-system.md → "Compact Setting Rows" for the full standard.
 */

import { useOutsideAlerter } from '@src/components/transcript/useClickOutside';
import CheckIcon from '@src/svgs/CheckIcon';
import CheckIconAlt from '@src/svgs/CheckIconAlt';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';

export interface MultiSelectOption<T extends string = string> {
  value: T;
  label: string;
  disabled?: boolean;
}

interface Props<T extends string> {
  label: string;
  options: MultiSelectOption<T>[];
  selected: Set<T>;
  onToggle: (value: T) => void;
  /** Summary text shown when nothing is selected. */
  emptyLabel?: string;
  /**
   * Single-select mode: choosing an option is a radio pick (the parent's
   * `onToggle` should replace the selection) and the checklist collapses after
   * the choice. Defaults to multi-select (toggle, stays open).
   */
  singleSelect?: boolean;
  /** data-testid applied to the row's trigger. */
  testId?: string;
}

export function MultiSelectRow<T extends string = string>({
  label,
  options,
  selected,
  onToggle,
  emptyLabel = 'None',
  singleSelect = false,
  testId,
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);

  // Close the floating menu when clicking/tapping anywhere outside this row
  // (e.g. the other setting row's trigger, or into the renderer iframe). Scoped
  // to the row so it never dismisses the enclosing panel, which owns its own
  // outside-click handling.
  const close = useCallback(() => setOpen(false), []);
  useOutsideAlerter(rowRef, close);

  const summary =
    options
      .filter(o => selected.has(o.value))
      .map(o => o.label)
      .join(', ') || emptyLabel;

  return (
    <Row ref={rowRef} $open={open}>
      <Trigger
        type="button"
        aria-expanded={open}
        data-testid={testId}
        onClick={() => setOpen(o => !o)}
      >
        <Label>{label}</Label>
        <Value $muted={summary === emptyLabel}>
          <Summary>{summary}</Summary>
          <Caret $open={open}>
            <ChevronDownIcon
              width="10"
              height="10"
              strokeColor="currentColor"
            />
          </Caret>
        </Value>
      </Trigger>

      {open && (
        <Checklist role="listbox" aria-multiselectable={!singleSelect}>
          {options.map(opt => {
            const isSelected = selected.has(opt.value);
            return (
              <Item
                key={opt.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                aria-disabled={opt.disabled}
                disabled={opt.disabled}
                $selected={isSelected}
                onClick={() => {
                  onToggle(opt.value);
                  if (singleSelect) setOpen(false);
                }}
              >
                {singleSelect ? (
                  <CheckSlot>
                    {isSelected && (
                      // Natural 0 0 6 6 viewBox (the path's own space) — a
                      // larger viewBox shoves the glyph into the top-left.
                      <CheckIcon
                        width="10"
                        height="10"
                        viewBox="0 0 6 6"
                        fillColor="var(--accent)"
                      />
                    )}
                  </CheckSlot>
                ) : (
                  // Design-system standard: gold checkbox + CheckIconAlt (bold
                  // dark stroke, built for the gold background) — see
                  // docs/design-system.md → "Selectable Items".
                  <Checkbox $checked={isSelected} aria-hidden>
                    {isSelected && <CheckIconAlt />}
                  </Checkbox>
                )}
                {opt.label}
              </Item>
            );
          })}
        </Checklist>
      )}
    </Row>
  );
}

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const Row = styled.div<{ $open?: boolean }>`
  position: relative;
  /* Lift the open row (and its floating menu) above later rows + the variant /
     footer content so the dropdown is never covered (z-index fix). */
  ${({ $open }) => $open && 'z-index: 5;'}
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid var(--border-subtle);

  &:last-child {
    border-bottom: none;
  }
`;

const Trigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
  width: 100%;
  background: none;
  border: none;
  padding: var(--spacing-sm) 0;
  cursor: pointer;
  font-family: var(--font-primary);
`;

const Label = styled.span`
  font-size: var(--text-xs);
  color: var(--text-secondary);
  white-space: nowrap;
`;

// Borderless pill matching the canvas "Preview" dropdown trigger
// (SafeZoneDropdown): quiet at rest, --surface-elevated on hover, gold caret.
const Value = styled.span<{ $muted: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  min-width: 0;
  max-width: 210px;
  padding: 4px 8px;
  border-radius: var(--radius);
  color: ${({ $muted }) =>
    $muted ? 'var(--text-muted)' : 'var(--foreground)'};
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  transition: background-color 150ms ease-out;

  ${Trigger}:hover & {
    background-color: var(--surface-elevated);
  }
`;

const Summary = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Caret = styled.span<{ $open: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  color: var(--accent);
  transition: transform var(--transition-fast);
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
`;

// Floating menu matching the canvas aspect-ratio dropdown (AspectDropDown):
// rounded-row popover overlaying content instead of pushing it, right-aligned
// under the trigger chip. Not portaled — it lives inside the row so clicks stay
// within the enclosing panel.
const Checklist = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 200px;
  width: max-content;
  max-width: 260px;
  padding: 4px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--background);
  box-shadow: var(--elevation-toast);
`;

const Item = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  min-height: 30px;
  padding: 0 var(--spacing-sm);
  border: none;
  border-radius: 6px;
  background: none;
  cursor: pointer;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  text-align: left;
  color: ${({ $selected }) =>
    $selected ? 'var(--foreground)' : 'var(--text-secondary)'};
  transition:
    background-color var(--transition-fast),
    color var(--transition-fast);

  &:hover:not(:disabled) {
    background-color: var(--surface-elevated);
    color: var(--foreground);
  }

  &:disabled {
    color: var(--text-muted);
    cursor: default;
  }
`;

const CheckSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  flex-shrink: 0;
`;

// Multi-select checkbox: an empty square that fills with the gold accent + a
// check when selected — a clearer "pick many" affordance than a bare check.
const Checkbox = styled.span<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  border-radius: 3px;
  border: 1.5px solid
    ${({ $checked }) => ($checked ? 'var(--accent)' : 'var(--border)')};
  background: ${({ $checked }) => ($checked ? 'var(--accent)' : 'transparent')};
  transition:
    background-color var(--transition-fast),
    border-color var(--transition-fast);
`;
